import {Matrix4, Vector3, Mesh, MeshPhongMaterial, MeshNormalMaterial, Object3D} from 'three'

class Renderable extends Object3D {
  constructor(geometry, info = {}) {
    super()
    this.toggle = this.toggle.bind(this)
    this.updateMaterials = this.updateMaterials.bind(this)
    this.center = this.center.bind(this)
    this.switchMode = this.switchMode.bind(this)
    this.info = info
    this.geometry = this.info.clone ? geometry.clone() : geometry
    this.url = this.info.url || `x-renderable://${this.id}`
    const left = this.info.name || this.info.url
    this.name = left || `model-${this.id}`
    if (this.info.center == null) {
      this.info.center = true
    }

    // Scale the object to an upper bound order of magnitude
    // if requested by info.autoscale
    this.info.scale = 1.0
    if (this.info.autoscale) {
      if (this.info.scaleTo == null) {
        this.info.scaleTo = 100
      }
      this.geometry.computeBoundingSphere()
      if (this.geometry.boundingSphere.radius > this.info.scaleTo) {
        this.info.scale = this.info.scaleTo / this.geometry.boundingSphere.radius
        const scaleMat = new Matrix4().makeScale(this.info.scale, this.info.scale, this.info.scale)
        this.geometry.applyMatrix4(scaleMat)
        this.geometry.computeBoundingSphere()
      }
    }

    const mesh = new Mesh(geometry)

    // Add myself
    this.mesh = mesh
    this.mesh.name = this.name
    this.add(this.mesh)

    // Add any parts
    for (const part of this.info.parts || []) {
      this.add(part)
    }

    if (this.info.center) {
      this.center()
    }

    this.switchMode(this.info.mode || 'solid')
  }

  toggle(status) {
    this.traverse(o => (o.visible = status))
  }

  updateMaterials(props) {
    if (!props) {
      return
    }
    this.traverse(o => {
      const result = []
      for (const k of Object.keys(props)) {
        const v = props[k]
        const x1 = o && o.mesh && o.mesh.material
        if (x1) {
          result.push((o.mesh.material[k] = v))
        }
      }
      return result
    })
  }

  center() {
    let bounds = undefined
    this.traverse(o => {
      const geom = o.geometry
      if (!geom) {
        return
      }

      geom.computeBoundingBox()

      if (bounds) {
        bounds.expandByPoint(geom.boundingBox.min)
        bounds.expandByPoint(geom.boundingBox.max)
      } else {
        bounds = geom.boundingBox.clone()
      }
    })

    const bb = bounds.clone()
    const center = bounds.getCenter(new Vector3(-1, -1, -1)).negate()
    const min = bounds.min.negate()
    window.debug(`Total centering transform: ${center.x}, ${center.y}, ${min.z}`)
    window.debug('Bounds: ', bb)
    const transform = new Matrix4().makeTranslation(center.x, center.y, min.z)

    this.traverse(o => {
      // Only process the mesh'es actually in the scene
      // instead of also the renderable containers
      // to avoid applying a transform twice per container.
      if (o.mesh) {
        return
      }
      if (!o.geometry) {
        return
      }
      o.geometry.applyMatrix4(transform)
      o.geometry.computeBoundingBox()
      o.geometry.computeBoundingSphere()
    })
    return this
  }

  switchMode(mode) {
    if (!mode) {
      mode = 'solid'
    }
    const materialOptions = {
      color: this.info.color || 0xffa07a, // The default color is 'Salmon'
      specular: this.info.specular || 0x999999,
      transparent: this.info.transparent != null ? this.info.transparent : false,
      opacity: this.info.opacity != null ? this.info.opacity : 1.0
    }

    switch (mode) {
      case 'wireframe':
        this.mesh.material = new MeshPhongMaterial(materialOptions)
        this.mesh.material.wireframe = true
        break
      case 'solid':
        this.mesh.material = new MeshPhongMaterial(materialOptions)
        this.mesh.material.wireframe = false
        break
      case 'normal':
        this.mesh.material = new MeshNormalMaterial(materialOptions)
        this.mesh.material.wireframe = false
        break
    }

    this.mesh.material.side = this.info.side
    this.mesh.material.needsUpdate = true
    this.geometry.buffersNeedUpdate = true
    this.geometry.uvsNeedUpdate = true
  }
}

export default Renderable
